import * as React from 'react';
import { ReactElement, ReactNode } from 'react';
import {
    Box,
    Collapse,
    List,
    ListItemIcon,
    MenuItem,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useSidebarState } from 'react-admin';

import { sidebarColors } from './appBarTheme';

interface Props {
    dense: boolean;
    handleToggle: () => void;
    icon: ReactElement;
    isOpen: boolean;
    name: string;
    children: ReactNode;
}

/**
 * A collapsible group of menu entries.
 *
 * Two changes worth knowing about. The group's own icon used to be REPLACED by
 * the chevron while the group was open, so an expanded group lost the only
 * thing identifying it; the icon now stays and the chevron sits on the right,
 * rotating to show state. And the label was `color="textSecondary"`, a dark
 * grey that only survived on the dark sidebar because App.css forced it white
 * -- it now takes the sidebar's own derived foreground.
 */
const SubMenu = (props: Props) => {
    const { handleToggle, isOpen, name, icon, children, dense } = props;
    const [sidebarIsOpen] = useSidebarState();

    const header = (
        <MenuItem
            dense={dense}
            onClick={handleToggle}
            sx={theme => {
                const { fg } = sidebarColors(theme);
                return {
                    mx: 1,
                    my: 0.25,
                    borderRadius: 2,
                    color: alpha(fg, 0.75),
                    '&:hover': { bgcolor: alpha(fg, 0.08), color: fg },
                    '& .MuiListItemIcon-root': { color: 'inherit' },
                };
            }}
        >
            <ListItemIcon sx={{ minWidth: 30, fontSize: 15 }}>{icon}</ListItemIcon>
            <Typography
                variant="inherit"
                sx={{
                    flex: 1,
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: '.12em',
                    textTransform: 'uppercase',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                }}
            >
                {name}
            </Typography>
            {sidebarIsOpen && (
                <ExpandMore
                    fontSize="small"
                    sx={{
                        transition: 'transform .2s ease',
                        transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                        opacity: 0.7,
                    }}
                />
            )}
        </MenuItem>
    );

    return (
        <Box sx={{ mb: 0.5 }}>
            {sidebarIsOpen || isOpen ? (
                header
            ) : (
                <Tooltip title={name} placement="right">
                    {header}
                </Tooltip>
            )}
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List
                    dense={dense}
                    component="div"
                    disablePadding
                    sx={theme => ({
                        position: 'relative',
                        ml: sidebarIsOpen ? 2.75 : 0,
                        pl: sidebarIsOpen ? 0.75 : 0,
                        // A hairline running down the group, so its entries read
                        // as belonging to it rather than as a flat list.
                        borderLeft: sidebarIsOpen
                            ? `1px solid ${alpha(sidebarColors(theme).fg, 0.15)}`
                            : 'none',
                    })}
                >
                    {children}
                </List>
            </Collapse>
        </Box>
    );
};

export default SubMenu;
